import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@roger/ui-components';
import { Button } from '@roger/ui-components';
import { Input } from '@roger/ui-components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Textarea } from '@roger/ui-components';
import { useToast } from '../hooks/useToastContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@roger/ui-components';

// Import generated types and API function for COB
import { checkCoordinationOfBenefitsStediCoordinationOfBenefitsPost } from '../services/api/sdk.gen';
import type {
  StediCobRequest,
  StediCobResponse,
  StediEligibilityProvider,
  StediCobSubscriber,
  StediAddress,
} from '../services/api/types.gen';
import { AxiosError } from 'axios';
import { NpiInput } from './NpiInput';

// Common test payers from Stedi's network
const stediPayerOptions = [
  { value: '60054', label: 'Aetna' },
  { value: '040', label: 'Anthem Blue Cross Blue Shield of CA' },
  { value: '62308', label: 'Cigna' },
  { value: '87726', label: 'UnitedHealthcare' },
];

const serviceTypeOptions = [
  { value: '30', label: '(30) Health Benefit Plan Coverage' },
  { value: '1', label: '(1) Medical Care' },
  { value: '33', label: '(33) Chiropractic' },
  { value: '35', label: '(35) Dental Care' },
  { value: '47', label: '(47) Hospital' },
  { value: '86', label: '(86) Emergency Services' },
  { value: '88', label: '(88) Pharmacy' },
  { value: '98', label: '(98) Professional (Physician) Visit - Office' },
];

const individualRelationshipCodeOptions = [
  { value: '01', label: '01 - Spouse' },
  { value: '19', label: '19 - Child' },
  { value: '34', label: '34 - Other Adult' },
];

const Stedi_COB_Testing: React.FC = () => {
  const { success, error } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<StediCobResponse | null>(null);
  const [activeTab, setActiveTab] = useState('request');

  const [formData, setFormData] = useState<StediCobRequest>({
    tradingPartnerServiceId: '60054', // Default to Aetna
    provider: {
      organizationName: 'Blossom Ridge Health',
      firstName: 'John',
      lastName: 'Smith',
      npi: '1417441361',
    },
    subscriber: {
      memberId: 'AETNA9wcSu',
      firstName: 'John',
      middleName: 'Michael',
      lastName: 'Doe',
      suffix: 'Jr',
      dateOfBirth: '1985-03-15',
      gender: 'M',
      ssn: '123456789',
    },
    dependent: null, // COB supports only one dependent - can be set via form
    encounter: {
      serviceTypeCode: '30',
      dateOfService: '2024-03-15',
      beginningDateOfService: null,
      endDateOfService: null,
    },
  });

  const handleProviderChange = (
    field: keyof StediEligibilityProvider,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      provider: {
        ...prev.provider,
        [field]: value,
      },
    }));
  };

  const handleSubscriberChange = (
    field: keyof StediCobSubscriber,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      subscriber: {
        ...prev.subscriber,
        [field]: value === '' ? '' : value, // Explicitly handle empty strings
      },
    }));
  };

  // Helper function to remove empty string fields from objects
  const removeEmptyFields = (obj: unknown): unknown => {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      const cleanedArray = obj.map(removeEmptyFields).filter(item => {
        // Remove null, undefined, empty strings, and empty objects
        if (item === null || item === undefined || item === '') {
          return false;
        }
        if (typeof item === 'object' && item !== null) {
          return Object.keys(item as Record<string, unknown>).length > 0;
        }
        return true;
      });
      return cleanedArray.length > 0 ? cleanedArray : undefined;
    }

    if (typeof obj === 'object') {
      const cleaned: Record<string, unknown> = {};
      Object.keys(obj as Record<string, unknown>).forEach(key => {
        const value = (obj as Record<string, unknown>)[key];

        // Skip empty strings, null, undefined, and whitespace-only strings
        if (
          value === '' ||
          value === null ||
          value === undefined ||
          (typeof value === 'string' && value.trim() === '')
        ) {
          return;
        }

        if (typeof value === 'object' && value !== null) {
          const cleanedValue = removeEmptyFields(value);
          // Only add non-empty objects and arrays
          if (cleanedValue !== undefined && cleanedValue !== null) {
            if (Array.isArray(cleanedValue)) {
              if (cleanedValue.length > 0) {
                cleaned[key] = cleanedValue;
              }
            } else if (
              Object.keys(cleanedValue as Record<string, unknown>).length > 0
            ) {
              cleaned[key] = cleanedValue;
            }
          }
        } else {
          // Add non-empty primitive values
          cleaned[key] = value;
        }
      });
      return cleaned;
    }

    return obj;
  };

  // Compute cleaned form data that updates with form changes
  const cleanedFormData = React.useMemo(() => {
    const cleaned = removeEmptyFields(formData) as StediCobRequest;
    // Ensure tradingPartnerServiceId is always included
    const result = {
      ...cleaned,
      tradingPartnerServiceId: formData.tradingPartnerServiceId,
    };
    // Force a deep clone to ensure React sees the change
    return JSON.parse(JSON.stringify(result));
  }, [formData]);

  const handleSubmit = async () => {
    setIsLoading(true);
    setResponse(null);

    try {
      // Validate required fields
      if (!formData.tradingPartnerServiceId) {
        throw new Error('Please select a payor');
      }

      // Use the generated COB API function with cleaned data
      const result =
        await checkCoordinationOfBenefitsStediCoordinationOfBenefitsPost({
          body: cleanedFormData,
        });

      if (result.data) {
        setResponse(result.data);
        setActiveTab('response'); // Switch to response tab
      }
      success('Stedi COB check completed successfully!');
    } catch (err: unknown) {
      console.error('Error:', err);
      if (err instanceof Error) {
        const errorMessage =
          err?.message || 'Failed to check coordination of benefits';
        error(errorMessage);
      } else if (err instanceof AxiosError) {
        const errorMessage =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to check coordination of benefits';
        error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateForDisplay = (dateStr: string) => {
    // Date is already in YYYY-MM-DD format, return as-is
    return dateStr || '';
  };

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>Stedi COB (Coordination of Benefits) Testing</CardTitle>
          <CardDescription>
            Test coordination of benefits checks using Stedi's JSON API format
            to identify primary and secondary payers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger value='request'>Request</TabsTrigger>
              <TabsTrigger value='response'>Response</TabsTrigger>
            </TabsList>

            <TabsContent value='request' className='space-y-6'>
              {/* Provider Information */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Provider Information</h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      htmlFor='organizationName'
                      className='block text-sm font-medium mb-2'
                    >
                      Organization Name
                    </label>
                    <Input
                      id='organizationName'
                      value={formData.provider.organizationName || ''}
                      onChange={e =>
                        handleProviderChange('organizationName', e.target.value)
                      }
                      placeholder='Healthcare Provider Name'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='npi'
                      className='block text-sm font-medium mb-2'
                    >
                      NPI (Optional)
                    </label>
                    <NpiInput
                      id='npi'
                      value={formData.provider.npi || ''}
                      onValueChange={npi => handleProviderChange('npi', npi)}
                      placeholder='1234567890'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='taxId'
                      className='block text-sm font-medium mb-2'
                    >
                      Tax ID (Optional)
                    </label>
                    <Input
                      id='taxId'
                      value={formData.provider.taxId || ''}
                      onChange={e =>
                        handleProviderChange('taxId', e.target.value)
                      }
                      placeholder='123456789'
                      maxLength={9}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='referenceIdentification'
                      className='block text-sm font-medium mb-2'
                    >
                      Taxonomy Code (Optional)
                    </label>
                    <Input
                      id='referenceIdentification'
                      value={formData.provider.referenceIdentification || ''}
                      onChange={e =>
                        handleProviderChange(
                          'referenceIdentification',
                          e.target.value
                        )
                      }
                      placeholder='207Q00000X'
                    />
                  </div>
                </div>
              </div>

              {/* Subscriber Information */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>
                  Subscriber Information
                </h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      htmlFor='memberId'
                      className='block text-sm font-medium mb-2'
                    >
                      Member ID
                    </label>
                    <Input
                      id='memberId'
                      value={formData.subscriber.memberId || ''}
                      onChange={e =>
                        handleSubscriberChange('memberId', e.target.value)
                      }
                      placeholder='123456789'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='gender'
                      className='block text-sm font-medium mb-2'
                    >
                      Gender
                    </label>
                    <Select
                      value={formData.subscriber.gender || ''}
                      onValueChange={(value: 'M' | 'F') =>
                        handleSubscriberChange('gender', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='M'>Male</SelectItem>
                        <SelectItem value='F'>Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label
                      htmlFor='firstName'
                      className='block text-sm font-medium mb-2'
                    >
                      First Name
                    </label>
                    <Input
                      id='firstName'
                      value={formData.subscriber.firstName || ''}
                      onChange={e =>
                        handleSubscriberChange('firstName', e.target.value)
                      }
                      placeholder='John'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='lastName'
                      className='block text-sm font-medium mb-2'
                    >
                      Last Name
                    </label>
                    <Input
                      id='lastName'
                      value={formData.subscriber.lastName || ''}
                      onChange={e =>
                        handleSubscriberChange('lastName', e.target.value)
                      }
                      placeholder='Doe'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='dateOfBirth'
                      className='block text-sm font-medium mb-2'
                    >
                      Date of Birth
                    </label>
                    <Input
                      id='dateOfBirth'
                      type='date'
                      value={formatDateForDisplay(
                        formData.subscriber.dateOfBirth || ''
                      )}
                      onChange={e =>
                        handleSubscriberChange('dateOfBirth', e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='groupNumber'
                      className='block text-sm font-medium mb-2'
                    >
                      Group Number (Optional)
                    </label>
                    <Input
                      id='groupNumber'
                      value={formData.subscriber.groupNumber || ''}
                      onChange={e =>
                        handleSubscriberChange('groupNumber', e.target.value)
                      }
                      placeholder='GRP123456'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='ssn'
                      className='block text-sm font-medium mb-2'
                    >
                      SSN (Required if no Member ID)
                    </label>
                    <Input
                      id='ssn'
                      value={formData.subscriber.ssn || ''}
                      onChange={e =>
                        handleSubscriberChange('ssn', e.target.value)
                      }
                      placeholder='123456789'
                      maxLength={9}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='middleName'
                      className='block text-sm font-medium mb-2'
                    >
                      Middle Name (Optional)
                    </label>
                    <Input
                      id='middleName'
                      value={formData.subscriber.middleName || ''}
                      onChange={e =>
                        handleSubscriberChange('middleName', e.target.value)
                      }
                      placeholder='M'
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='suffix'
                      className='block text-sm font-medium mb-2'
                    >
                      Suffix (Optional)
                    </label>
                    <Input
                      id='suffix'
                      value={formData.subscriber.suffix || ''}
                      onChange={e =>
                        handleSubscriberChange('suffix', e.target.value)
                      }
                      placeholder='Jr.'
                    />
                  </div>
                </div>

                {/* Additional Subscriber Fields (collapsed by default) */}
                <details className='mt-4'>
                  <summary className='cursor-pointer text-sm font-medium text-muted-foreground'>
                    Additional Subscriber Information (Optional)
                  </summary>
                  <div className='grid grid-cols-2 gap-4 mt-4'>
                    <div>
                      <label
                        htmlFor='medicaidRecipientId'
                        className='block text-sm font-medium mb-2'
                      >
                        Medicaid Recipient ID
                      </label>
                      <Input
                        id='medicaidRecipientId'
                        value={
                          formData.subscriber
                            .medicaidRecipientIdentificationNumber || ''
                        }
                        onChange={e =>
                          handleSubscriberChange(
                            'medicaidRecipientIdentificationNumber',
                            e.target.value
                          )
                        }
                        placeholder='Medicaid ID'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='caseNumber'
                        className='block text-sm font-medium mb-2'
                      >
                        Case Number
                      </label>
                      <Input
                        id='caseNumber'
                        value={formData.subscriber.caseNumber || ''}
                        onChange={e =>
                          handleSubscriberChange('caseNumber', e.target.value)
                        }
                        placeholder='Case #'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='idCard'
                        className='block text-sm font-medium mb-2'
                      >
                        ID Card Number
                      </label>
                      <Input
                        id='idCard'
                        value={formData.subscriber.idCard || ''}
                        onChange={e =>
                          handleSubscriberChange('idCard', e.target.value)
                        }
                        placeholder='ID card #'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='birthSequenceNumber'
                        className='block text-sm font-medium mb-2'
                      >
                        Birth Sequence Number
                      </label>
                      <Input
                        id='birthSequenceNumber'
                        value={formData.subscriber.birthSequenceNumber || ''}
                        onChange={e =>
                          handleSubscriberChange(
                            'birthSequenceNumber',
                            e.target.value
                          )
                        }
                        placeholder='For multiple births'
                      />
                    </div>
                  </div>

                  {/* Subscriber Address */}
                  <div className='mt-4'>
                    <h5 className='text-sm font-medium mb-2'>
                      Subscriber Address (Optional)
                    </h5>
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <label
                          htmlFor='subscriberAddress1'
                          className='block text-sm font-medium mb-2'
                        >
                          Address Line 1
                        </label>
                        <Input
                          id='subscriberAddress1'
                          value={formData.subscriber.address?.address1 || ''}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                address: {
                                  ...prev.subscriber.address,
                                  address1: e.target.value,
                                  city: prev.subscriber.address?.city || '',
                                } as StediAddress,
                              },
                            }))
                          }
                          placeholder='123 Main St'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='subscriberAddress2'
                          className='block text-sm font-medium mb-2'
                        >
                          Address Line 2
                        </label>
                        <Input
                          id='subscriberAddress2'
                          value={formData.subscriber.address?.address2 || ''}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                address: {
                                  ...prev.subscriber.address,
                                  address1:
                                    prev.subscriber.address?.address1 || '',
                                  address2: e.target.value,
                                  city: prev.subscriber.address?.city || '',
                                } as StediAddress,
                              },
                            }))
                          }
                          placeholder='Apt 2B'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='subscriberCity'
                          className='block text-sm font-medium mb-2'
                        >
                          City
                        </label>
                        <Input
                          id='subscriberCity'
                          value={formData.subscriber.address?.city || ''}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                address: {
                                  ...prev.subscriber.address,
                                  address1:
                                    prev.subscriber.address?.address1 || '',
                                  city: e.target.value,
                                } as StediAddress,
                              },
                            }))
                          }
                          placeholder='Houston'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='subscriberState'
                          className='block text-sm font-medium mb-2'
                        >
                          State
                        </label>
                        <Input
                          id='subscriberState'
                          value={formData.subscriber.address?.state || ''}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                address: {
                                  ...prev.subscriber.address,
                                  address1:
                                    prev.subscriber.address?.address1 || '',
                                  city: prev.subscriber.address?.city || '',
                                  state: e.target.value,
                                } as StediAddress,
                              },
                            }))
                          }
                          placeholder='TX'
                          maxLength={2}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='subscriberPostalCode'
                          className='block text-sm font-medium mb-2'
                        >
                          Postal Code
                        </label>
                        <Input
                          id='subscriberPostalCode'
                          value={formData.subscriber.address?.postalCode || ''}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                address: {
                                  ...prev.subscriber.address,
                                  address1:
                                    prev.subscriber.address?.address1 || '',
                                  city: prev.subscriber.address?.city || '',
                                  postalCode: e.target.value,
                                } as StediAddress,
                              },
                            }))
                          }
                          placeholder='77001'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='subscriberCountryCode'
                          className='block text-sm font-medium mb-2'
                        >
                          Country Code
                        </label>
                        <Input
                          id='subscriberCountryCode'
                          value={formData.subscriber.address?.countryCode || ''}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                address: {
                                  ...prev.subscriber.address,
                                  address1:
                                    prev.subscriber.address?.address1 || '',
                                  city: prev.subscriber.address?.city || '',
                                  countryCode: e.target.value,
                                } as StediAddress,
                              },
                            }))
                          }
                          placeholder='US'
                          maxLength={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Identification */}
                  <div className='mt-4'>
                    <h5 className='text-sm font-medium mb-2'>
                      Additional Identification (Optional)
                    </h5>
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <label
                          htmlFor='planNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Plan Number
                        </label>
                        <Input
                          id='planNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.planNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  planNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Plan number'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='policyNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Policy Number
                        </label>
                        <Input
                          id='policyNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.policyNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  policyNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Policy number'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='memberIdentificationNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Member Identification Number
                        </label>
                        <Input
                          id='memberIdentificationNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.memberIdentificationNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  memberIdentificationNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Member ID'
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='contractNumber'
                          className='block text-sm font-medium mb-2'
                        >
                          Contract Number
                        </label>
                        <Input
                          id='contractNumber'
                          value={
                            formData.subscriber.additionalIdentification
                              ?.contractNumber || ''
                          }
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              subscriber: {
                                ...prev.subscriber,
                                additionalIdentification: {
                                  ...prev.subscriber.additionalIdentification,
                                  contractNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder='Contract number'
                        />
                      </div>
                    </div>
                  </div>
                </details>
              </div>

              {/* Dependents Section */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Dependent (Optional)</h3>
                <p className='text-sm text-muted-foreground'>
                  COB checks support only one dependent at a time. Add a
                  dependent without a unique member ID for COB checking.
                </p>
                <div className='space-y-4'>
                  {formData.dependent ? (
                    <div className='border rounded-lg p-4 space-y-4'>
                      <div className='flex justify-between items-center mb-4'>
                        <h4 className='font-medium'>Dependent</h4>
                        <Button
                          type='button'
                          variant='destructive'
                          size='sm'
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              dependent: null,
                            }));
                          }}
                        >
                          Remove
                        </Button>
                      </div>

                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Relationship
                          </label>
                          <Select
                            value={
                              formData.dependent.individualRelationshipCode ||
                              ''
                            }
                            onValueChange={value => {
                              setFormData(prev => ({
                                ...prev,
                                dependent: prev.dependent
                                  ? {
                                      ...prev.dependent,
                                      individualRelationshipCode: value,
                                    }
                                  : null,
                              }));
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder='Select relationship' />
                            </SelectTrigger>
                            <SelectContent>
                              {individualRelationshipCodeOptions.map(option => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Member ID (Optional)
                          </label>
                          <Input
                            value={formData.dependent.memberId || ''}
                            onChange={e => {
                              setFormData(prev => ({
                                ...prev,
                                dependent: prev.dependent
                                  ? {
                                      ...prev.dependent,
                                      memberId: e.target.value,
                                    }
                                  : null,
                              }));
                            }}
                            placeholder='Member ID'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            First Name (Required)
                          </label>
                          <Input
                            value={formData.dependent.firstName || ''}
                            onChange={e => {
                              setFormData(prev => ({
                                ...prev,
                                dependent: prev.dependent
                                  ? {
                                      ...prev.dependent,
                                      firstName: e.target.value,
                                    }
                                  : null,
                              }));
                            }}
                            placeholder='First name'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Last Name (Required)
                          </label>
                          <Input
                            value={formData.dependent.lastName || ''}
                            onChange={e => {
                              setFormData(prev => ({
                                ...prev,
                                dependent: prev.dependent
                                  ? {
                                      ...prev.dependent,
                                      lastName: e.target.value,
                                    }
                                  : null,
                              }));
                            }}
                            placeholder='Last name'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Date of Birth (Required)
                          </label>
                          <Input
                            type='date'
                            value={formData.dependent.dateOfBirth || ''}
                            onChange={e => {
                              setFormData(prev => ({
                                ...prev,
                                dependent: prev.dependent
                                  ? {
                                      ...prev.dependent,
                                      dateOfBirth: e.target.value,
                                    }
                                  : null,
                              }));
                            }}
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Middle Name (Optional)
                          </label>
                          <Input
                            value={formData.dependent.middleName || ''}
                            onChange={e => {
                              setFormData(prev => ({
                                ...prev,
                                dependent: prev.dependent
                                  ? {
                                      ...prev.dependent,
                                      middleName: e.target.value,
                                    }
                                  : null,
                              }));
                            }}
                            placeholder='Middle name'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Suffix (Optional)
                          </label>
                          <Input
                            value={formData.dependent.suffix || ''}
                            onChange={e => {
                              setFormData(prev => ({
                                ...prev,
                                dependent: prev.dependent
                                  ? {
                                      ...prev.dependent,
                                      suffix: e.target.value,
                                    }
                                  : null,
                              }));
                            }}
                            placeholder='Jr, Sr, etc.'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            Gender
                          </label>
                          <Select
                            value={formData.dependent.gender || ''}
                            onValueChange={value => {
                              setFormData(prev => ({
                                ...prev,
                                dependent: prev.dependent
                                  ? {
                                      ...prev.dependent,
                                      gender: value as 'M' | 'F',
                                    }
                                  : null,
                              }));
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value='M'>Male</SelectItem>
                              <SelectItem value='F'>Female</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className='block text-sm font-medium mb-2'>
                            SSN (Optional)
                          </label>
                          <Input
                            value={formData.dependent.ssn || ''}
                            onChange={e => {
                              setFormData(prev => ({
                                ...prev,
                                dependent: prev.dependent
                                  ? {
                                      ...prev.dependent,
                                      ssn: e.target.value,
                                    }
                                  : null,
                              }));
                            }}
                            placeholder='123456789'
                            maxLength={9}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          dependent: {
                            individualRelationshipCode: '19',
                            memberId: '',
                            firstName: '',
                            middleName: '',
                            lastName: '',
                            suffix: '',
                            dateOfBirth: '',
                            gender: 'M' as const,
                            ssn: '',
                          },
                        }));
                      }}
                    >
                      Add Dependent
                    </Button>
                  )}
                </div>
              </div>

              {/* Payor Selection */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Payor Information</h3>
                <div>
                  <label
                    htmlFor='payor'
                    className='block text-sm font-medium mb-2'
                  >
                    Trading Partner (Payor)
                  </label>
                  <Select
                    value={formData.tradingPartnerServiceId}
                    onValueChange={value =>
                      setFormData(prev => ({
                        ...prev,
                        tradingPartnerServiceId: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select a payor' />
                    </SelectTrigger>
                    <SelectContent>
                      {stediPayerOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Service Information */}
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>Service Information</h3>

                {/* COB-specific encounter fields */}
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label
                      htmlFor='dateOfService'
                      className='block text-sm font-medium mb-2'
                    >
                      Date of Service (Single Date)
                    </label>
                    <Input
                      id='dateOfService'
                      type='date'
                      value={formData.encounter?.dateOfService || ''}
                      onChange={e => {
                        setFormData(prev => ({
                          ...prev,
                          encounter: {
                            ...prev.encounter,
                            dateOfService: e.target.value,
                            // Clear date range if single date is set
                            beginningDateOfService: undefined,
                            endDateOfService: undefined,
                          },
                        }));
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='serviceTypeCode'
                      className='block text-sm font-medium mb-2'
                    >
                      Service Type Code
                    </label>
                    <Select
                      value={formData.encounter?.serviceTypeCode || '30'}
                      onValueChange={value =>
                        setFormData(prev => ({
                          ...prev,
                          encounter: {
                            ...prev.encounter,
                            serviceTypeCode: value, // COB uses single service type code
                          },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select service type' />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypeOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Date Range Option */}
                <div className='space-y-2'>
                  <p className='text-sm text-muted-foreground'>
                    OR use a date range for the service period:
                  </p>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label
                        htmlFor='beginningDateOfService'
                        className='block text-sm font-medium mb-2'
                      >
                        Beginning Date of Service
                      </label>
                      <Input
                        id='beginningDateOfService'
                        type='date'
                        value={formData.encounter?.beginningDateOfService || ''}
                        onChange={e => {
                          setFormData(prev => ({
                            ...prev,
                            encounter: {
                              ...prev.encounter,
                              beginningDateOfService: e.target.value,
                              // Clear single date if range is set
                              dateOfService: undefined,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='endDateOfService'
                        className='block text-sm font-medium mb-2'
                      >
                        End Date of Service
                      </label>
                      <Input
                        id='endDateOfService'
                        type='date'
                        value={formData.encounter?.endDateOfService || ''}
                        onChange={e => {
                          setFormData(prev => ({
                            ...prev,
                            encounter: {
                              ...prev.encounter,
                              endDateOfService: e.target.value,
                              // Clear single date if range is set
                              dateOfService: undefined,
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={isLoading}
                className='w-full'
              >
                {isLoading ? 'Checking COB...' : 'Submit COB Check'}
              </Button>

              {/* Request Preview */}
              <div className='mt-6'>
                <label className='block text-sm font-medium mb-2'>
                  Request Preview (JSON) - Empty fields removed
                </label>
                <pre
                  key={JSON.stringify(cleanedFormData)}
                  className='bg-muted p-4 rounded-md text-xs overflow-auto font-mono max-h-96'
                >
                  {JSON.stringify(cleanedFormData, null, 2)}
                </pre>
              </div>
            </TabsContent>

            <TabsContent value='response' className='space-y-6'>
              {response ? (
                <>
                  {/* COB Status Summary */}
                  {response.coordinationOfBenefits ? (
                    <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                      <h3 className='text-lg font-semibold text-blue-800 mb-2'>
                        COB Status
                      </h3>
                      <div className='space-y-2'>
                        <p className='text-blue-700'>
                          <span className='font-medium'>Classification:</span>{' '}
                          {response.coordinationOfBenefits.classification}
                        </p>
                        <p className='text-blue-700'>
                          <span className='font-medium'>
                            COB Instance Exists:
                          </span>{' '}
                          {response.coordinationOfBenefits.instanceExists
                            ? 'Yes'
                            : 'No'}
                        </p>
                        {response.coordinationOfBenefits.primacyDetermined !==
                          undefined && (
                          <p className='text-blue-700'>
                            <span className='font-medium'>
                              Primacy Determined:
                            </span>{' '}
                            {response.coordinationOfBenefits.primacyDetermined
                              ? 'Yes'
                              : 'No'}
                          </p>
                        )}
                        {response.coordinationOfBenefits.coverageOverlap !==
                          undefined && (
                          <p className='text-blue-700'>
                            <span className='font-medium'>
                              Coverage Overlap:
                            </span>{' '}
                            {response.coordinationOfBenefits.coverageOverlap
                              ? 'Yes'
                              : 'No'}
                          </p>
                        )}
                        {response.coordinationOfBenefits.benefitOverlap !==
                          undefined && (
                          <p className='text-blue-700'>
                            <span className='font-medium'>
                              Benefit Overlap:
                            </span>{' '}
                            {response.coordinationOfBenefits.benefitOverlap
                              ? 'Yes'
                              : 'No'}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className='p-4 bg-gray-50 border border-gray-200 rounded-lg'>
                      <h3 className='text-lg font-semibold text-gray-800'>
                        No COB Information Available
                      </h3>
                    </div>
                  )}

                  {/* Error Display */}
                  {response.errors && response.errors.length > 0 && (
                    <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
                      <h3 className='text-lg font-semibold text-red-800 mb-2'>
                        Errors
                      </h3>
                      {response.errors.map((err, idx) => (
                        <div key={idx} className='mb-2'>
                          <p className='text-red-700 font-medium'>
                            {err.code}: {err.description}
                          </p>
                          {err.followupActionCode && (
                            <p className='text-red-600 text-sm'>
                              {err.followupActionCode}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Plan Date Information */}
                  {response.planDateInformation && (
                    <div>
                      <h3 className='text-lg font-semibold mb-3'>
                        Plan Coverage Dates
                      </h3>
                      <div className='bg-muted p-4 rounded-md space-y-2'>
                        {response.planDateInformation.planBegin && (
                          <p>
                            <span className='font-medium'>Coverage Start:</span>{' '}
                            {response.planDateInformation.planBegin}
                          </p>
                        )}
                        {response.planDateInformation.planEnd && (
                          <p>
                            <span className='font-medium'>Coverage End:</span>{' '}
                            {response.planDateInformation.planEnd}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Subscriber Information */}
                  {response.subscriber && (
                    <div>
                      <h3 className='text-lg font-semibold mb-3'>
                        Subscriber Information
                      </h3>
                      <div className='bg-muted p-4 rounded-md space-y-2'>
                        <p>
                          <span className='font-medium'>Name:</span>{' '}
                          {response.subscriber.firstName}{' '}
                          {response.subscriber.middleName &&
                            response.subscriber.middleName + ' '}
                          {response.subscriber.lastName}
                        </p>
                        <p>
                          <span className='font-medium'>Member ID:</span>{' '}
                          {response.subscriber.memberId}
                        </p>
                        {response.subscriber.groupNumber && (
                          <p>
                            <span className='font-medium'>Group Number:</span>{' '}
                            {response.subscriber.groupNumber}
                          </p>
                        )}
                        {response.subscriber.dateOfBirth && (
                          <p>
                            <span className='font-medium'>Date of Birth:</span>{' '}
                            {response.subscriber.dateOfBirth}
                          </p>
                        )}
                        {response.subscriber.gender && (
                          <p>
                            <span className='font-medium'>Gender:</span>{' '}
                            {response.subscriber.gender === 'M'
                              ? 'Male'
                              : response.subscriber.gender === 'F'
                                ? 'Female'
                                : response.subscriber.gender}
                          </p>
                        )}
                        {response.subscriber.ssn && (
                          <p>
                            <span className='font-medium'>SSN:</span>{' '}
                            {response.subscriber.ssn}
                          </p>
                        )}
                        {response.subscriber.planDescription && (
                          <p>
                            <span className='font-medium'>Plan:</span>{' '}
                            {response.subscriber.planDescription}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Dependent Information */}
                  {response.dependent && (
                    <div>
                      <h3 className='text-lg font-semibold mb-3'>
                        Dependent Information
                      </h3>
                      <div className='bg-muted p-4 rounded-md space-y-2'>
                        <p>
                          <span className='font-medium'>Name:</span>{' '}
                          {response.dependent.firstName}{' '}
                          {response.dependent.middleName &&
                            response.dependent.middleName + ' '}
                          {response.dependent.lastName}
                        </p>
                        {response.dependent.dateOfBirth && (
                          <p>
                            <span className='font-medium'>Date of Birth:</span>{' '}
                            {response.dependent.dateOfBirth}
                          </p>
                        )}
                        {response.dependent.gender && (
                          <p>
                            <span className='font-medium'>Gender:</span>{' '}
                            {response.dependent.gender === 'M'
                              ? 'Male'
                              : response.dependent.gender === 'F'
                                ? 'Female'
                                : response.dependent.gender}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Payor Information */}
                  {response.payor && (
                    <div>
                      <h3 className='text-lg font-semibold mb-3'>
                        Payor Information
                      </h3>
                      <div className='bg-muted p-4 rounded-md space-y-2'>
                        {response.payor.payorName && (
                          <p>
                            <span className='font-medium'>Payor Name:</span>{' '}
                            {response.payor.payorName}
                          </p>
                        )}
                        {response.payor.payorId && (
                          <p>
                            <span className='font-medium'>Payor ID:</span>{' '}
                            {response.payor.payorId}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Benefits Information with Related Entities */}
                  {response.benefitsInformation &&
                    response.benefitsInformation.length > 0 && (
                      <div>
                        <h3 className='text-lg font-semibold mb-3'>
                          Benefits Information & Related Entities
                        </h3>
                        <div className='space-y-3'>
                          {response.benefitsInformation.map((benefit, idx) => (
                            <div
                              key={idx}
                              className='bg-muted p-4 rounded-md space-y-3'
                            >
                              <div>
                                <p className='font-medium'>
                                  {benefit.name || benefit.code}
                                </p>
                                {benefit.coverageLevelCode && (
                                  <p className='text-sm'>
                                    Coverage Level: {benefit.coverageLevelCode}
                                  </p>
                                )}
                                {benefit.serviceTypeCodes &&
                                  benefit.serviceTypeCodes.length > 0 && (
                                    <p className='text-sm'>
                                      Service Types:{' '}
                                      {benefit.serviceTypeCodes.join(', ')}
                                    </p>
                                  )}
                                {benefit.benefitAmount && (
                                  <p className='text-sm'>
                                    Amount: ${benefit.benefitAmount}
                                  </p>
                                )}
                                {benefit.benefitPercent && (
                                  <p className='text-sm'>
                                    Percentage: {benefit.benefitPercent}%
                                  </p>
                                )}
                                {benefit.description && (
                                  <p className='text-sm text-muted-foreground'>
                                    {benefit.description}
                                  </p>
                                )}
                              </div>

                              {/* Benefits Related Entities (Payers) - Not available in current schema */}
                              {/* Commented out as benefitsRelatedEntities field is not available in StediBenefitsInformation */}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Response Metadata */}
                  {response.meta && (
                    <div>
                      <h3 className='text-lg font-semibold mb-3'>
                        Response Metadata
                      </h3>
                      <div className='bg-muted p-4 rounded-md space-y-2'>
                        {response.meta.applicationMode && (
                          <p>
                            <span className='font-medium'>
                              Application Mode:
                            </span>{' '}
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                response.meta.applicationMode === 'production'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {response.meta.applicationMode}
                            </span>
                          </p>
                        )}
                        {response.meta.traceId && (
                          <p>
                            <span className='font-medium'>Trace ID:</span>{' '}
                            <code className='bg-gray-100 px-1 py-0.5 rounded text-xs'>
                              {response.meta.traceId}
                            </code>
                          </p>
                        )}
                        {response.meta.outboundTraceId && (
                          <p>
                            <span className='font-medium'>
                              Outbound Trace ID:
                            </span>{' '}
                            <code className='bg-gray-100 px-1 py-0.5 rounded text-xs'>
                              {response.meta.outboundTraceId}
                            </code>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Raw X12 Response */}
                  {response.x12 && (
                    <div>
                      <label className='block text-sm font-medium mb-2'>
                        Raw X12 271 Response
                      </label>
                      <Textarea
                        value={response.x12}
                        readOnly
                        className='font-mono text-xs'
                        rows={10}
                      />
                    </div>
                  )}

                  {/* Full JSON Response */}
                  <div>
                    <label className='block text-sm font-medium mb-2'>
                      Full JSON Response
                    </label>
                    <pre className='bg-muted p-4 rounded-md text-xs overflow-auto font-mono max-h-96'>
                      {JSON.stringify(response, null, 2)}
                    </pre>
                  </div>
                </>
              ) : (
                <div className='text-center py-12 text-muted-foreground'>
                  No response yet. Submit a COB check to see results.
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Stedi_COB_Testing;
